import { useMemo, useState } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { Button } from "@/shared/components/ui/button";
import { Info, Package, Download, FileSpreadsheet, Share2, Plus, ChevronRight } from "lucide-react";
import { useSnackbar } from "notistack";

import { TOAST } from "@/shared/constants/toastMessages";
import {
  CATEGORY_ICONS,
  INVENTORY_CATEGORY_GROUPS,
  type InventoryCategoryGroup,
} from "@/shared/constants/estateCategories";
import { DonutChart } from "@/shared/components/DonutChart";
import { getCategoryStyle, getGroupHex } from "@/shared/utils/categoryColors";
import { DocumentBadge } from "@/shared/components/DocumentBadge";
import { getEstateDonutData, groupItemsByCategory } from "@/shared/utils/estateMath";
import { formatDollars } from "@/shared/utils/formatters";
import { generateInventoryPdf } from "@/shared/utils/generateInventoryPdf";
import { useEstateItems } from "@/shared/hooks/useEstateItems";
import { AddEstateItemModal } from "@/shared/components/EstateItems/AddEstateItemModal";
import type { EstateItem, SourceDocument } from "@/shared/types/estateTypes";

type EstateItemWithSource = EstateItem & {
  source_document?: SourceDocument | null;
};

function renderGroupCard(
  group: InventoryCategoryGroup,
  groupedItems: Record<string, EstateItemWithSource[]>,
  fallbackNameLabel: string,
  fallbackValueLabel: string,
  onOpenDocument: (doc: SourceDocument) => void,
  onSelectItem: (item: EstateItemWithSource) => void,
) {
  const items = group.subCategories.flatMap((sub) => groupedItems[sub] || []);
  if (items.length === 0) return null;

  const nameLabel = fallbackNameLabel;
  const valueLabel = fallbackValueLabel;
  const itemCount = items.length;
  const totalValue = items
    .filter((i) => !i.value_label)
    .reduce((sum, item) => sum + Number(item.balance), 0);

  return (
    <div key={group.title} className="bg-white rounded-xl border border-primary overflow-hidden">
      <div className="flex items-center justify-between p-6">
        <div className="flex items-baseline gap-2">
          <h3 className="text-lg font-semibold text-primary">{group.title}</h3>
          <span className="text-sm text-secondary">
            {itemCount} {itemCount === 1 ? "item" : "items"}
          </span>
        </div>
        <div className="text-right">
          <p className="text-lg font-semibold text-primary">
            {formatDollars({ amount: totalValue })}
          </p>
        </div>
      </div>
      <div>
        <table className="w-full text-sm">
          <colgroup>
            <col style={{ width: "28%" }} />
            <col style={{ width: "18%" }} />
            <col style={{ width: "18%" }} />
            <col style={{ width: "28%" }} />
            <col style={{ width: "8%" }} />
          </colgroup>
          <thead>
            <tr className="bg-subtle border-y border-primary">
              <th className="text-left text-sm font-medium text-secondary uppercase tracking-wider py-3 pl-4 pr-2">
                {nameLabel}
              </th>
              <th className="text-left text-sm font-medium text-secondary uppercase tracking-wider py-3 px-2">
                Type
              </th>
              <th className="text-left text-sm font-medium text-secondary uppercase tracking-wider py-3 px-2">
                {valueLabel}
              </th>
              <th className="text-left text-sm font-medium text-secondary uppercase tracking-wider py-3 px-2">
                Source
              </th>
              <th className="py-3 pr-4 pl-2" aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const ItemIcon =
                CATEGORY_ICONS[item.category as keyof typeof CATEGORY_ICONS] || Package;
              const itemStyle = getCategoryStyle(item.category);
              return (
                <tr
                  key={item.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => onSelectItem(item)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onSelectItem(item);
                    }
                  }}
                  className="border-b border-stone-100 last:border-b-0 cursor-pointer transition-colors hover:bg-subtle"
                >
                  <td className="py-4 pl-4 pr-2">
                    <div className="flex items-center gap-2.5">
                      <div
                        className={`w-6 h-6 rounded-md ${itemStyle.bgClass} flex items-center justify-center flex-shrink-0`}
                      >
                        <ItemIcon className={`w-3.5 h-3.5 ${itemStyle.textClass}`} />
                      </div>
                      <div>
                        <div className="font-medium text-primary">{item.item_name}</div>
                        {item.identifiers?.account_last4 && (
                          <div className="text-secondary text-sm mt-0.5">
                            Account ending ••{item.identifiers.account_last4}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-2 text-primary">{item.item_type || "—"}</td>
                  <td className="py-4 px-2">
                    <div className="font-medium text-primary">
                      {item.value_label
                        ? item.value_label
                        : formatDollars({ amount: Number(item.balance) })}
                    </div>
                  </td>
                  <td className="py-4 px-2">
                    {item.source_document ? (
                      <DocumentBadge
                        sourceDocument={item.source_document}
                        variant="long"
                        onClick={() => onOpenDocument(item.source_document!)}
                        color="stone"
                        size="sm"
                      />
                    ) : (
                      <span className="text-tertiary">—</span>
                    )}
                  </td>
                  <td className="py-4 pr-4 pl-2 text-right">
                    <ChevronRight className="h-4 w-4 text-tertiary inline-block" />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface LawyerEstateInventoryTabProps {
  estateId: number;
  onOpenDocument: (doc: SourceDocument) => void;
  onSelectItem: (item: EstateItem) => void;
}

export function LawyerEstateInventoryTab({
  estateId,
  onOpenDocument,
  onSelectItem,
}: LawyerEstateInventoryTabProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [isDownloading, setIsDownloading] = useState(false);
  const [addClassification, setAddClassification] = useState<"asset" | "debt" | null>(null);

  const { assets: rawAssets, debts: rawDebts } = useEstateItems(estateId);

  // Mirror postloss EstateBreakdown math exactly:
  //   - drop items with notes === "__MARKED_AS_NONE__"
  //   - donut + per-category legend totals: sum(Math.abs(balance)) over ALL items
  //     (no value_label filter), restricted to known categories
  //   - center "Net Estate Value": valuable items only (no value_label), raw balance,
  //     totalAssets - totalDebts (matches edge function summary.netValue)
  const assets = useMemo(
    () =>
      (rawAssets as EstateItemWithSource[]).filter((item) => item.notes !== "__MARKED_AS_NONE__"),
    [rawAssets],
  );
  const debts = useMemo(
    () =>
      (rawDebts as EstateItemWithSource[]).filter((item) => item.notes !== "__MARKED_AS_NONE__"),
    [rawDebts],
  );

  const donutData = useMemo(() => getEstateDonutData([...assets, ...debts]), [assets, debts]);
  const groupedAssets = useMemo(() => groupItemsByCategory(assets), [assets]);
  const groupedDebts = useMemo(() => groupItemsByCategory(debts), [debts]);

  const hasItems = assets.length > 0 || debts.length > 0;

  const renderAddButton = (kind: "asset" | "debt") => (
    <Button variant="brand" onClick={() => setAddClassification(kind)}>
      <Plus className="w-4 h-4 mr-2" />
      {kind === "asset" ? "Add asset" : "Add liability"}
    </Button>
  );

  const addItemModal = addClassification && (
    <AddEstateItemModal isOpen onClose={() => setAddClassification(null)} estateId={estateId} />
  );

  if (!hasItems) {
    return (
      <>
        <div className="bg-white rounded-xl border border-primary p-6">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-secondary">No financial items added yet.</p>
            <div className="flex items-center gap-2">
              {renderAddButton("asset")}
              {renderAddButton("debt")}
            </div>
          </div>
        </div>
        {addItemModal}
      </>
    );
  }

  const { segments, netValue, totalAssets, totalDebts, assetGroups, debtGroups } = donutData;

  const handleDownloadPdf = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      await generateInventoryPdf({
        assets,
        debts,
        summary: {
          netValue,
          totalAssets,
          totalDebts,
          categories: [],
          hasItems: true,
        },
      });
    } catch (err) {
      console.error("Failed to generate inventory PDF:", err);
      enqueueSnackbar("Could not generate PDF. Please try again.", { variant: "error" });
    } finally {
      setIsDownloading(false);
    }
  };

  const assetCategoryGroups = INVENTORY_CATEGORY_GROUPS.filter((g) => g.classification === "asset");
  const debtCategoryGroups = INVENTORY_CATEGORY_GROUPS.filter((g) => g.classification === "debt");

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border border-primary p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-medium text-secondary">Estimated Estate Value</p>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="w-3.5 h-3.5 text-tertiary cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-[280px]">
                  <p className="font-light text-sm mb-3 text-primary">
                    The total identified assets minus total identified liabilities
                  </p>
                  <p className="text-sm font-light text-primary">
                    Note: This is the current financial picture based on what has been added so far
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleDownloadPdf} disabled={isDownloading}>
              <Download className="w-4 h-4" />
              {isDownloading ? "Downloading..." : "Download as PDF"}
            </Button>
            <Button
              variant="outline"
              onClick={() => enqueueSnackbar(TOAST.COMING_SOON, { variant: "info" })}
            >
              <FileSpreadsheet className="w-4 h-4" />
              Export to Excel
            </Button>
            <Button
              variant="outline"
              onClick={() => enqueueSnackbar(TOAST.COMING_SOON, { variant: "info" })}
            >
              <Share2 className="w-4 h-4" />
              Share
            </Button>
          </div>
        </div>
        {(() => {
          return (
            <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2 mt-6">
              <div className="flex items-center justify-center">
                <DonutChart
                  segments={segments}
                  centerLabel="Net Estate Value"
                  centerValue={formatDollars({ amount: netValue })}
                />
              </div>
              <div className="flex flex-col gap-6">
                {assetGroups.length > 0 && (
                  <div className="flex flex-col gap-3">
                    <p className="flex items-center gap-2 text-sm font-semibold text-primary">
                      Assets{" "}
                      <span className="text-sm font-normal text-secondary">
                        {formatDollars({ amount: totalAssets })}
                      </span>
                    </p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                      {assetGroups.map((g) => (
                        <div key={g.group} className="flex items-center gap-2">
                          <span
                            className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                            style={{ backgroundColor: getGroupHex(g.group) }}
                          />
                          <span className="text-sm text-primary">{g.group}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {debtGroups.length > 0 && <div className="border-t border-primary" />}
                {debtGroups.length > 0 && (
                  <div className="flex flex-col gap-3">
                    <p className="flex items-center gap-2 text-sm font-semibold text-primary">
                      Liabilities{" "}
                      <span className="text-sm font-normal text-secondary">
                        {formatDollars({ amount: totalDebts })}
                      </span>
                    </p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                      {debtGroups.map((g) => (
                        <div key={g.group} className="flex items-center gap-2">
                          <span
                            className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                            style={{ backgroundColor: "#e7e5e4" }}
                          />
                          <span className="text-sm text-primary">{g.group}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })()}
      </div>

      {(() => {
        const visibleAssetGroups = assetCategoryGroups.filter((g) =>
          g.subCategories.some((sub) => (groupedAssets[sub]?.length || 0) > 0),
        );
        if (visibleAssetGroups.length === 0) return null;
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-sans font-bold text-primary">Assets</h2>
              {renderAddButton("asset")}
            </div>
            {visibleAssetGroups.map((group) =>
              renderGroupCard(group, groupedAssets, "Item", "Value", onOpenDocument, onSelectItem),
            )}
          </div>
        );
      })()}

      {(() => {
        const visibleDebtGroups = debtCategoryGroups.filter((g) =>
          g.subCategories.some((sub) => (groupedDebts[sub]?.length || 0) > 0),
        );
        if (visibleDebtGroups.length === 0) return null;
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-sans font-bold text-primary">Liabilities</h2>
              {renderAddButton("debt")}
            </div>
            {visibleDebtGroups.map((group) =>
              renderGroupCard(
                group,
                groupedDebts,
                "Liability",
                "Balance",
                onOpenDocument,
                onSelectItem,
              ),
            )}
          </div>
        );
      })()}

      {addItemModal}
    </div>
  );
}
